import React from 'react';
import { BookOpen, ChevronDown, ChevronUp, Check, FileText, Palette, Printer, PenTool } from 'lucide-react';
import { Input } from './ui/input';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
        data-testid="btn-toggle-editorial"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-[#272b96] to-[#aa2aff] rounded-lg">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-semibold">{title}</span>
          {badge && (
            <span className="bg-[#aa2aff]/20 text-[#aa2aff] text-xs font-bold px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
}

function EditorialServiceCard({ selected, onToggle, icon: Icon, title, price, description, children, testId }) {
  return (
    <div
      className={`border rounded-xl p-4 cursor-pointer transition-all ${
        selected
          ? 'border-[#aa2aff] bg-[#aa2aff]/10'
          : 'border-white/10 bg-white/5 hover:border-white/20'
      }`}
      onClick={onToggle}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg flex-shrink-0 ${selected ? 'bg-[#aa2aff]/30' : 'bg-white/10'}`}>
          <Icon className={`w-5 h-5 ${selected ? 'text-[#aa2aff]' : 'text-slate-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className={`font-semibold text-sm ${selected ? 'text-white' : 'text-slate-300'}`}>{title}</h4>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selected ? 'bg-[#aa2aff] border-[#aa2aff]' : 'border-slate-500'
            }`}>
              {selected && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
          <p className="text-[#aa2aff] font-bold text-sm mt-1">{price}</p>
          {description && (
            <p className="text-slate-400 text-xs mt-1 leading-relaxed">{description}</p>
          )}
          {selected && children && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function EditorialSection({
  isOpen,
  onToggle,
  selectedMaquetacion,
  onMaquetacionChange,
  selectedCorreccion,
  onCorreccionChange,
  correccionCuartillas,
  onCorreccionCuartillasChange,
  selectedPortadaFisica,
  onPortadaFisicaChange,
  selectedImpresion,
  onImpresionChange,
  impresionCantidad,
  onImpresionCantidadChange,
  selectedGhostWriting,
  onGhostWritingChange,
  formatPrice,
  currency,
  settings = {},
}) {
  const priceMaquetacion = settings.price_editorial_maquetacion || 10000;
  const priceCorreccionPerCuartilla = settings.price_editorial_correccion || 97;
  const pricePortadaFisica = settings.price_editorial_portada_fisica || 8500;
  const priceGhostWriting = settings.price_editorial_ghost_writing || 287000;

  // Tiered pricing for printing
  const getImpresionPricePerUnit = (cantidad) => {
    if (cantidad >= 251) return settings.price_editorial_impresion_251 || 83;
    if (cantidad >= 101) return settings.price_editorial_impresion_101 || 97;
    return settings.price_editorial_impresion || 180;
  };
  const impresionPricePerUnit = getImpresionPricePerUnit(impresionCantidad || 0);

  const correccionTotal = selectedCorreccion ? priceCorreccionPerCuartilla * (correccionCuartillas || 0) : 0;
  const impresionTotal = selectedImpresion ? impresionPricePerUnit * (impresionCantidad || 0) : 0;

  const subtotal =
    (selectedMaquetacion ? priceMaquetacion : 0) +
    correccionTotal +
    (selectedPortadaFisica ? pricePortadaFisica : 0) +
    impresionTotal +
    (selectedGhostWriting ? priceGhostWriting : 0);

  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection
      title="Servicios Editoriales"
      icon={BookOpen}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-3">
        {/* Maquetación */}
        <EditorialServiceCard
          selected={selectedMaquetacion}
          onToggle={() => onMaquetacionChange(!selectedMaquetacion)}
          icon={FileText}
          title="Maquetación físico y digital"
          price={`${formatPrice(priceMaquetacion)} + IVA`}
          description="Diseño y maquetación profesional de tu libro para versión impresa y digital."
          testId="editorial-maquetacion"
        />

        {/* Corrección de estilo */}
        <EditorialServiceCard
          selected={selectedCorreccion}
          onToggle={() => onCorreccionChange(!selectedCorreccion)}
          icon={BookOpen}
          title="Corrección de ortografía y gramática"
          price={`${formatPrice(priceCorreccionPerCuartilla)}/cuartilla + IVA`}
          description="Corrección ortotipográfica. Corrección de errores ortográficos y de puntuación. Ajuste de ortografía a normas RAE. Aplicación de recursos tipográficos. Unificación de criterios. Control de viudas y huérfanas, repetición de sílabas en líneas consecutivas, revisión de blancos y márgenes. Revisión del sumario y correspondencia con la obra."
          testId="editorial-correccion"
        >
          <div className="space-y-2">
            <label className="text-slate-400 text-xs">Número de cuartillas</label>
            <Input
              type="number"
              min="1"
              value={correccionCuartillas || ''}
              onChange={(e) => onCorreccionCuartillasChange(parseInt(e.target.value) || 0)}
              placeholder="Ej: 200"
              className="bg-[#0a0e2a] border-white/20 text-white w-32"
              data-testid="input-correccion-cuartillas"
            />
            {correccionCuartillas > 0 && (
              <p className="text-[#aa2aff] text-xs font-semibold">
                {correccionCuartillas} cuartillas = {formatPrice(correccionTotal)}
              </p>
            )}
          </div>
        </EditorialServiceCard>

        {/* Diseño de portada libro físico */}
        <EditorialServiceCard
          selected={selectedPortadaFisica}
          onToggle={() => onPortadaFisicaChange(!selectedPortadaFisica)}
          icon={Palette}
          title="Diseño de portada de libro físico"
          price={`${formatPrice(pricePortadaFisica)} + IVA`}
          description="Diseño profesional de portada, lomo y contraportada para tu libro impreso."
          testId="editorial-portada-fisica"
        />

        {/* Impresión de libros */}
        <EditorialServiceCard
          selected={selectedImpresion}
          onToggle={() => onImpresionChange(!selectedImpresion)}
          icon={Printer}
          title="Impresión de libros"
          price={`${formatPrice(impresionPricePerUnit)}/libro + IVA`}
          description="1-100 copias: $180/libro · 101-250: $97/libro · 251+: $83/libro"
          testId="editorial-impresion"
        >
          <div className="space-y-2">
            <label className="text-slate-400 text-xs">Cantidad de libros</label>
            <Input
              type="number"
              min="1"
              value={impresionCantidad || ''}
              onChange={(e) => onImpresionCantidadChange(parseInt(e.target.value) || 0)}
              placeholder="Ej: 100"
              className="bg-[#0a0e2a] border-white/20 text-white w-32"
              data-testid="input-impresion-cantidad"
            />
            {impresionCantidad > 0 && (
              <p className="text-[#aa2aff] text-xs font-semibold">
                {impresionCantidad} libros × {formatPrice(impresionPricePerUnit)} = {formatPrice(impresionTotal)}
              </p>
            )}
          </div>
        </EditorialServiceCard>

        {/* Ghost Writing */}
        <EditorialServiceCard
          selected={selectedGhostWriting}
          onToggle={() => onGhostWritingChange(!selectedGhostWriting)}
          icon={PenTool}
          title="Ghost Writing"
          price={`${formatPrice(priceGhostWriting)} + IVA`}
          description="Escritura profesional de tu libro por un escritor fantasma. Incluye investigación, redacción completa y revisiones."
          testId="editorial-ghost-writing"
        />
      </div>
    </CollapsibleSection>
  );
}

export default EditorialSection;
